/**
 * Arbitration Service
 * 
 * API client for dispute arbitration module
 */

import apiClient from '../utils/apiClient';

// Types
export enum DisputeType {
  ORDER = 'order',
  ORDER_ITEM = 'order_item',
  INSPECTION = 'inspection'
}

export enum DisputeStatus {
  PENDING = 'pending_admin_approval',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  INVESTIGATING = 'investigating',
  RESOLVED = 'resolved',
  CLOSED = 'closed'
}

export enum ArbitrationAction {
  APPROVE = 'approve',
  REJECT = 'reject',
  SEND_MESSAGE = 'send_message',
  CLOSE_CHAT = 'close_chat',
  REFUND_ORDER = 'refund_order',
  RESOLVE = 'resolve',
  REFUND_ITEM = 'refund_item',
  CHANGE_ITEM_STATUS = 'change_item_status',
  FORCE_DELIVER = 'force_deliver',
  REQUEST_REDELIVERY = 'request_redelivery',
  // Inspection specific
  REFUND_INSPECTION_FEE = 'refund_inspection_fee',
  OVERRIDE_INSPECTION = 'override_inspection',
  PENALIZE_INSPECTOR = 'penalize_inspector'
}

export interface IParticipant {
  userId: string;
  name: string;
  email?: string;
  role: 'buyer' | 'seller' | 'admin' | 'party_a' | 'party_b';
  avatar?: string;
}

export interface IChatMessage {
  id: string;
  senderId: string;
  senderName: string;
  senderRole: string;
  content: string;
  messageType: 'text' | 'image' | 'file' | 'system';
  timestamp: string;
  isRead: boolean;
  file?: {
    name: string;
    url: string;
    type: string;
    size: number;
  };
  // پیام‌های سیستمی (مثل کارت «نتیجه داوری» با action: 'dispute_verdict')
  systemData?: {
    action: string;
    metadata?: any;
  };
}

export interface IFinancialInfo {
  amount: number;
  shippingCost?: number;
  inspectionFee?: number;
  inspectorShippingCost?: number;
  refundableAmount: number;
  currency: string;
}

export interface IDisputeReference {
  orderId?: string;
  orderNumber?: string;
  orderItemId?: string;
  productTitle?: string;
  productId?: string;
  storeId?: string;
  storeName?: string;
  inspectorStoreId?: string;
  inspectorStoreName?: string;
  inspectionStatus?: string;
  // ✅ Multi-unit dispute info — when buyer disputes a specific subset of
  // a multi-quantity order item, admin needs to know which units + per-unit price.
  disputedQuantity?: number;
  totalQuantity?: number;
  unitPrice?: number;
}

export interface IDisputeChat {
  id: string;
  chatId: string;
  type: DisputeType;
  status: DisputeStatus;
  title: string;
  description: string;
  reference: IDisputeReference;
  participants: IParticipant[];
  financial?: IFinancialInfo;
  disputeReason: string;
  disputeDescription: string;
  // Order item status for arbitration actions
  itemStatus?: string;
  createdAt: string;
  updatedAt?: string;
  approvedAt?: string;
  resolvedAt?: string;
  totalMessages: number;
  lastMessage?: IChatMessage;
  availableActions: ArbitrationAction[];
}

export interface IDisputeStats {
  total: number;
  byType: Record<DisputeType, number>;
  byStatus: Record<DisputeStatus, number>;
}

export interface IActionPayload {
  reason?: string;
  resolution?: string;
  amount?: number;
  applyPenalty?: boolean;
  penaltyPoints?: number;
  restrictUser?: boolean;
  message?: string;
  // For changing item status
  newStatus?: string;
  // For force delivery
  deliveryConfirmed?: boolean;
  accountsToDeliver?: number;
  // For inspection disputes
  refundInspectionFee?: boolean;
  overrideResult?: 'approved' | 'rejected';
  inspectorPenaltyPoints?: number;
}

// Labels
export const ActionLabels: Record<ArbitrationAction, string> = {
  [ArbitrationAction.APPROVE]: 'تأیید اختلاف',
  [ArbitrationAction.REJECT]: 'رد اختلاف',
  [ArbitrationAction.SEND_MESSAGE]: 'ارسال پیام',
  [ArbitrationAction.CLOSE_CHAT]: 'بستن چت',
  [ArbitrationAction.REFUND_ORDER]: 'بازپرداخت سفارش',
  [ArbitrationAction.RESOLVE]: 'حل اختلاف',
  [ArbitrationAction.REFUND_ITEM]: 'بازپرداخت آیتم',
  [ArbitrationAction.CHANGE_ITEM_STATUS]: 'تغییر وضعیت آیتم',
  [ArbitrationAction.FORCE_DELIVER]: 'تأیید تحویل اجباری',
  [ArbitrationAction.REQUEST_REDELIVERY]: 'درخواست تحویل مجدد',
  [ArbitrationAction.REFUND_INSPECTION_FEE]: 'بازپرداخت هزینه بازرسی',
  [ArbitrationAction.OVERRIDE_INSPECTION]: 'تغییر نتیجه بازرسی',
  [ArbitrationAction.PENALIZE_INSPECTOR]: 'جریمه بازرس'
};

export const StatusLabels: Record<DisputeStatus, string> = {
  [DisputeStatus.PENDING]: 'در انتظار بررسی',
  [DisputeStatus.APPROVED]: 'تأیید شده',
  [DisputeStatus.REJECTED]: 'رد شده',
  [DisputeStatus.INVESTIGATING]: 'در حال بررسی',
  [DisputeStatus.RESOLVED]: 'حل شده',
  [DisputeStatus.CLOSED]: 'بسته شده'
};

export const TypeLabels: Record<DisputeType, string> = {
  [DisputeType.ORDER]: 'سفارش',
  [DisputeType.ORDER_ITEM]: 'آیتم سفارش',
  [DisputeType.INSPECTION]: 'بازرسی'
};

export const StatusColors: Record<DisputeStatus, 'warning' | 'info' | 'error' | 'success' | 'default'> = {
  [DisputeStatus.PENDING]: 'warning',
  [DisputeStatus.APPROVED]: 'info',
  [DisputeStatus.REJECTED]: 'error',
  [DisputeStatus.INVESTIGATING]: 'info',
  [DisputeStatus.RESOLVED]: 'success',
  [DisputeStatus.CLOSED]: 'default'
};

export const TypeColors: Record<DisputeType, 'primary' | 'secondary' | 'info' | 'warning' | 'error'> = {
  [DisputeType.ORDER]: 'primary',
  [DisputeType.ORDER_ITEM]: 'secondary',
  [DisputeType.INSPECTION]: 'error'
};

// API Functions
const arbitrationService = {
  /**
   * Get all disputes
   */
  async getDisputes(filters?: {
    type?: DisputeType;
    status?: DisputeStatus;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<IDisputeChat[]> {
    const response = await apiClient.get('/admin/arbitration/disputes', { params: filters });
    return response.data.data || [];
  },

  /**
   * Get single dispute
   */
  async getDispute(chatId: string): Promise<IDisputeChat | null> {
    const response = await apiClient.get(`/admin/arbitration/disputes/${chatId}`);
    return response.data.data || null;
  },

  /**
   * Get chat messages
   */
  async getMessages(chatId: string, page = 1, limit = 50): Promise<IChatMessage[]> {
    const response = await apiClient.get(`/admin/arbitration/disputes/${chatId}/messages`, {
      params: { page, limit }
    });
    return response.data.data || [];
  },

  /**
   * Send message
   */
  async sendMessage(chatId: string, content: string, imageUrl?: string): Promise<IChatMessage> {
    const payload: any = {
      content,
      messageType: imageUrl ? 'image' : 'text'
    };
    
    if (imageUrl) {
      payload.imageUrl = imageUrl;
    }
    
    const response = await apiClient.post(`/admin/arbitration/disputes/${chatId}/messages`, payload);
    return response.data.data;
  },

  /**
   * Get available actions
   */
  async getAvailableActions(chatId: string): Promise<ArbitrationAction[]> {
    const response = await apiClient.get(`/admin/arbitration/disputes/${chatId}/actions`);
    return response.data.data || [];
  },

  /**
   * Execute action
   */
  async executeAction(chatId: string, action: ArbitrationAction, payload: IActionPayload): Promise<{
    success: boolean;
    message: string;
    data?: any;
  }> {
    const response = await apiClient.post(`/admin/arbitration/disputes/${chatId}/actions/${action}`, payload);
    return {
      success: response.data.status === 'success',
      message: response.data.message,
      data: response.data.data
    };
  },

  /**
   * Get statistics
   */
  async getStats(): Promise<IDisputeStats> {
    const response = await apiClient.get('/admin/arbitration/stats');
    return response.data.data;
  }
};

export default arbitrationService;

